import { useEffect, useMemo, useState } from "react";
import { MessageCircle, Timer } from "lucide-react";
import { Reveal } from "./Reveal";
import { WA_PROMO } from "@/data/content";

const MARQUEE_ITEMS = Array(8).fill(
  "Limited-Time Promotion — 50% Off Consultation Services"
);

export default function PromoBanner() {
  const target = useMemo(() => Date.now() + 47 * 3600e3 + 23 * 60e3 + 11e3, []);
  const [left, setLeft] = useState(target - Date.now());

  useEffect(() => {
    const t = setInterval(() => setLeft(Math.max(0, target - Date.now())), 1000);
    return () => clearInterval(t);
  }, [target]);

  const d = Math.floor(left / 86400e3);
  const h = Math.floor((left % 86400e3) / 3600e3);
  const m = Math.floor((left % 3600e3) / 60e3);
  const s = Math.floor((left % 60e3) / 1e3);
  const pad = (n) => String(n).padStart(2, "0");

  return (
    <section id="promo" data-testid="promo-section" className="relative">
      <div className="marquee-hover-pause overflow-hidden border-y border-line bg-surface py-4">
        <div
          className="animate-marquee flex whitespace-nowrap"
          style={{ "--marquee-speed": "60s" }}
        >
          {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((t, i) => (
            <span
              key={i}
              className={`mx-8 font-display text-3xl font-black uppercase tracking-tight sm:text-4xl ${
                i % 2 === 0 ? "text-outline" : "text-brand"
              }`}
            >
              {t} <span className="mx-4 text-brand">✕</span>
            </span>
          ))}
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <Reveal>
          <div className="card-trace relative overflow-hidden rounded-2xl border border-line bg-panel p-8 sm:p-12">
            <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-brand/10 blur-3xl" />
            <div className="relative flex flex-col items-start justify-between gap-10 lg:flex-row lg:items-center">
              <div className="max-w-xl">
                <span className="inline-flex items-center gap-2 rounded-full border border-brand/40 bg-brand/10 px-3.5 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-brand">
                  <Timer size={12} />
                  Limited-Time Promotion
                </span>
                <h3 className="mt-5 font-display text-3xl font-black uppercase leading-none tracking-tight text-ink sm:text-4xl">
                  50% Off <span className="text-brand">Consultation</span> Services
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-mute sm:text-base">
                  Contact our official WhatsApp support team today to learn more
                  about our educational consultation services.
                </p>
              </div>

              <div className="flex flex-col items-start gap-6 sm:items-end">
                <div
                  data-testid="promo-countdown"
                  className="flex items-center gap-2 font-mono"
                >
                  {[
                    [d, "Days"],
                    [pad(h), "Hrs"],
                    [pad(m), "Min"],
                    [pad(s), "Sec"],
                  ].map(([v, label], i) => (
                    <div
                      key={label}
                      className="flex items-center gap-2"
                    >
                      <div className="flex flex-col items-center rounded-lg border border-line bg-surface px-3.5 py-2.5">
                        <span className="text-xl font-bold tabular-nums text-ink sm:text-2xl">
                          {v}
                        </span>
                        <span className="text-[9px] uppercase tracking-[0.2em] text-dim">
                          {label}
                        </span>
                      </div>
                      {i < 3 && <span className="text-brand">:</span>}
                    </div>
                  ))}
                </div>
                <a
                  data-testid="promo-whatsapp-claim-button"
                  href={WA_PROMO}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2.5 rounded-full bg-wa px-7 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.18em] text-white transition-transform duration-300 hover:scale-[1.03]"
                >
                  <MessageCircle size={15} />
                  Claim 50% Off on WhatsApp
                </a>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
